import { createCloudCoreInstance } from "../utils/axiosInstances";

export interface SemanticMap {
  id: string;
  name: string;
  shortName: string;
  track: {
    type: string;
    features: Array<{
      type: string;
      geometry: {
        type: string;
        coordinates: Array<[number, number]>;
      };
      properties: {
        type: string;
        stroke: string;
        "stroke-width": number;
        "stroke-opacity": number;
      };
    }>;
  };
  missions: string[];
  stops: string[];
  bbox: [number, number, number, number];
  version: string;
  localizationMap: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface StationPayload {
  name: string;
  short_name: string;
  semantic_map: string;
  latitude: number;
  longitude: number;
  utm_x: number;
  utm_y: number;
  utm_zone: string;
  initialization: string;
  marker_color: string;
  marker_size: string;
}

// Helper function to handle API errors consistently
const handleApiError = (error: any, operation: string): never => {
  console.error(`Error ${operation}:`, error);

  if (error.response) {
    // Server responded with error status
    const errorMessage =
      error.response.data?.detail ||
      error.response.data?.message ||
      `Server error: ${error.response.status}`;
    throw new Error(errorMessage);
  } else if (error.request) {
    // Request was made but no response received
    throw new Error("No response from server");
  } else {
    // Something else happened
    throw new Error(error.message || `Failed to ${operation}`);
  }
};

export const getSemanticMaps = async (
  fleetId: string
): Promise<SemanticMap[]> => {
  try {
    const cloudCoreInstance = createCloudCoreInstance();
    if (!cloudCoreInstance) {
      throw new Error("CLOUD_CORE_API_URL not configured");
    }

    const response = await cloudCoreInstance.get<
      PaginatedResponse<SemanticMap>
    >(`/semantic_maps/?fleet=${fleetId}`);
    return response.data.results;
  } catch (error) {
    console.error("Error fetching semantic maps:", error);
    throw error;
  }
};

export const createStation = async (payload: StationPayload): Promise<{ success: boolean; data: any }> => {
  try {
    const cloudCoreInstance = createCloudCoreInstance();
    if (!cloudCoreInstance) {
      throw new Error("CLOUD_CORE_API_URL not configured");
    }

    const response = await cloudCoreInstance.post("/stops/", payload);

    // Check if the response is successful
    if (response.status >= 200 && response.status < 300) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error: any) {
    handleApiError(error, "creating station");
    // This line will never be reached, but satisfies TypeScript
    throw error;
  }
};

export const updateStation = async (
  stationId: string,
  payload: Partial<StationPayload>
): Promise<{ success: boolean; data: any }> => {
  try {
    const cloudCoreInstance = createCloudCoreInstance();
    if (!cloudCoreInstance) {
      throw new Error("CLOUD_CORE_API_URL not configured");
    }

    const response = await cloudCoreInstance.patch(
      `/stops/${stationId}/`,
      payload
    );

    // Check if the response is successful
    if (response.status >= 200 && response.status < 300) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error: any) {
    handleApiError(error, "updating station");
    // This line will never be reached, but satisfies TypeScript
    throw error;
  }
};

export const deleteStation = async (stationId: string): Promise<{ success: boolean; data: any }> => {
  try {
    const cloudCoreInstance = createCloudCoreInstance();
    if (!cloudCoreInstance) {
      throw new Error("CLOUD_CORE_API_URL not configured");
    }

    const response = await cloudCoreInstance.delete(`/stops/${stationId}/`);

    // Check if the response is successful
    if (response.status >= 200 && response.status < 300) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error: any) {
    handleApiError(error, "deleting station");
    // This line will never be reached, but satisfies TypeScript
    throw error;
  }
};

// List all stations for a specific semantic map
export const getStationsBySemanticMap = async (semanticMapId: string) => {
  try {
    const cloudCoreInstance = createCloudCoreInstance();
    if (!cloudCoreInstance) {
      throw new Error("CLOUD_CORE_API_URL not configured");
    }

    const response = await cloudCoreInstance.get(
      `/stops/?semantic_map=${semanticMapId}`
    );
    return response.data.results;
  } catch (error) {
    console.error("Error fetching stations by semantic map:", error);
    throw error;
  }
};
